import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GridSortModel } from '@mui/x-data-grid-premium';

/**
 * URL-persisted state. Behaves like `useState`, but the value is stored in a URL
 * query param so a browser refresh (or a shared link) restores it. Each piece of
 * state gets its own readable param via a small codec.
 *
 * The URL is the single source of truth: `value` is derived from the current
 * search params (so back/forward navigation just works). When the value equals the
 * default the param is dropped, keeping the default view's URL clean.
 *
 * `history: 'push'` adds a history entry so Back undoes the change; 'replace' (the
 * default) does not. Anything typed into must stay 'replace' — one entry per keystroke.
 */
let inFlight: { from: string; to: URLSearchParams } | null = null;

export function useUrlState<T>(
  key: string,
  defaultValue: T,
  codec: UrlCodec<T>,
  history: 'push' | 'replace' = 'replace',
  /** Params dropped in the same write — e.g. the page number, when the report changes. */
  clears: string[] = [],
): [T, (next: T | ((prev: T) => T)) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  // Freeze the default and codec from first render so identity stays stable.
  const defaultRef = useRef(defaultValue);
  const codecRef = useRef(codec);
  const clearsRef = useRef(clears);

  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  const raw = searchParams.get(key);
  const value = useMemo(
    () => (raw == null ? defaultRef.current : safeDecode(codecRef.current, raw, defaultRef.current)),
    [raw],
  );

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      // setSearchParams takes its options eagerly and the updater lazily, so the
      // no-op check belongs here.
      const at = paramsRef.current;
      const cur = at.get(key);
      const curVal = cur == null ? defaultRef.current : safeDecode(codecRef.current, cur, defaultRef.current);
      const enc = codecRef.current.encode(typeof next === 'function' ? (next as (p: T) => T)(curVal) : next);
      const after = new URLSearchParams(at);
      if (enc === codecRef.current.encode(defaultRef.current)) after.delete(key);
      else after.set(key, enc);
      const changesKey = after.toString() !== at.toString();
      if (changesKey) clearsRef.current.forEach((k) => after.delete(k));
      const unchanged = after.toString() === at.toString();

      setSearchParams(
        (prev) => {
          // react-router gives the updater the CURRENT location's params, so two writes
          // in a tick both start from the same base and the last would drop the first.
          const sp = new URLSearchParams(inFlight?.from === prev.toString() ? inFlight.to : prev);
          const before = sp.toString();
          if (enc === codecRef.current.encode(defaultRef.current)) sp.delete(key);
          else sp.set(key, enc);
          if (sp.toString() !== before) clearsRef.current.forEach((k) => sp.delete(k));
          if (!inFlight) queueMicrotask(() => { inFlight = null; });
          inFlight = { from: prev.toString(), to: new URLSearchParams(sp) };
          return sp;
        },
        { replace: history === 'replace' || unchanged },
      );
    },
    [key, setSearchParams, history],
  );

  return [value, setValue];
}

function safeDecode<T>(codec: UrlCodec<T>, raw: string, fallback: T): T {
  try {
    return codec.decode(raw);
  } catch {
    return fallback;
  }
}

// ── Codecs ──────────────────────────────────────────────────────────────────

export interface UrlCodec<T> {
  encode: (v: T) => string;
  decode: (s: string) => T;
}

function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export const codecs = {
  str: { encode: (v: string) => v, decode: (s: string) => s } as UrlCodec<string>,

  num: { encode: (v: number) => String(v), decode: (s: string) => Number(s) } as UrlCodec<number>,

  bool: { encode: (v: boolean) => (v ? '1' : '0'), decode: (s: string) => s === '1' } as UrlCodec<boolean>,

  /** Generic JSON codec for arrays/objects (tag ids, entity selections, filters, …). */
  json: <T>(): UrlCodec<T> => ({
    encode: (v: T) => JSON.stringify(v),
    decode: (s: string) => JSON.parse(s) as T,
  }),

  /** `[Date|null, Date|null]` ⇄ `"YYYY-MM-DD..YYYY-MM-DD"` (local date parts). */
  dateRange: {
    encode: ([a, b]: [Date | null, Date | null]) => `${a ? fmtLocalDate(a) : ''}..${b ? fmtLocalDate(b) : ''}`,
    decode: (s: string): [Date | null, Date | null] => {
      const [a, b] = s.split('..');
      return [parseLocalDate(a), parseLocalDate(b)];
    },
  } as UrlCodec<[Date | null, Date | null]>,

  sortModel: {
    encode: (v: GridSortModel) => JSON.stringify(v),
    decode: (s: string) => JSON.parse(s) as GridSortModel,
  } as UrlCodec<GridSortModel>,

  /** `string[]` ⇄ `"a,b,c"` — readable, and a legacy single value decodes as one entry. */
  csv: {
    encode: (v: string[]) => v.filter(Boolean).join(','),
    decode: (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean),
  } as UrlCodec<string[]>,

  /** `csv` that folds case, so pre-#184 `?status=ACTIVE` links still match. Never for ids. */
  statusCsv: {
    encode: (v: string[]) => v.filter(Boolean).map((x) => x.toLowerCase()).join(','),
    decode: (s: string) => s.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean),
  } as UrlCodec<string[]>,

  /** `{page,pageSize}` ⇄ `"page.pageSize"` — one param, readable, updated atomically. */
  pageModel: {
    encode: ({ page, pageSize }: { page: number; pageSize: number }) => `${page}.${pageSize}`,
    decode: (s: string) => {
      const [p, ps] = s.split('.').map(Number);
      return { page: Number.isFinite(p) ? p : 0, pageSize: Number.isFinite(ps) ? ps : 100 };
    },
  } as UrlCodec<{ page: number; pageSize: number }>,
};
